import { ChacaUtils } from "../../../../../utils";
import { SQLExtensionValues } from "../sql-types";
import { Route } from "./route";

const POSTGRES_RESERVED_WORDS = [
  "all",
  "analyse",
  "analyze",
  "and",
  "any",
  "array",
  "as",
  "asc",
  "asymmetric",
  "both",
  "case",
  "cast",
  "check",
  "collate",
  "column",
  "constraint",
  "create",
  "current_catalog",
  "current_date",
  "current_role",
  "current_time",
  "current_timestamp",
  "current_user",
  "default",
  "deferrable",
  "desc",
  "distinct",
  "do",
  "else",
  "end",
  "except",
  "false",
  "fetch",
  "for",
  "foreign",
  "from",
  "grant",
  "group",
  "having",
  "in",
  "initially",
  "intersect",
  "into",
  "lateral",
  "leading",
  "limit",
  "localtime",
  "localtimestamp",
  "not",
  "null",
  "offset",
  "on",
  "only",
  "or",
  "order",
  "placing",
  "primary",
  "references",
  "returning",
  "select",
  "session_user",
  "some",
  "symmetric",
  "table",
  "then",
  "to",
  "trailing",
  "true",
  "union",
  "unique",
  "user",
  "using",
  "variadic",
  "when",
  "where",
  "window",
  "with",
];

const MYSQL_RESERVED_WORDS = [
  "accessible",
  "add",
  "all",
  "alter",
  "analyze",
  "and",
  "as",
  "asc",
  "asensitive",
  "before",
  "between",
  "bigint",
  "binary",
  "blob",
  "both",
  "by",
  "call",
  "cascade",
  "case",
  "change",
  "char",
  "character",
  "check",
  "collate",
  "column",
  "condition",
  "constraint",
  "continue",
  "convert",
  "create",
  "cross",
  "cube",
  "cume_dist",
  "current_date",
  "current_time",
  "current_timestamp",
  "current_user",
  "cursor",
  "database",
  "databases",
  "day_hour",
  "day_microsecond",
  "day_minute",
  "day_second",
  "dec",
  "decimal",
  "declare",
  "default",
  "delayed",
  "delete",
  "dense_rank",
  "desc",
  "describe",
  "deterministic",
  "distinct",
  "distinctrow",
  "div",
  "double",
  "drop",
  "dual",
  "each",
  "else",
  "elseif",
  "empty",
  "enclosed",
  "escaped",
  "except",
  "exists",
  "exit",
  "explain",
  "false",
  "fetch",
  "first_value",
  "float",
  "float4",
  "float8",
  "for",
  "force",
  "foreign",
  "from",
  "fulltext",
  "function",
  "generated",
  "get",
  "grant",
  "group",
  "grouping",
  "groups",
  "having",
  "high_priority",
  "hour_microsecond",
  "hour_minute",
  "hour_second",
  "if",
  "ignore",
  "in",
  "index",
  "infile",
  "inner",
  "inout",
  "insensitive",
  "insert",
  "int",
  "int1",
  "int2",
  "int3",
  "int4",
  "int8",
  "integer",
  "interval",
  "into",
  "io_after_gtids",
  "io_before_gtids",
  "is",
  "iterate",
  "join",
  "json_table",
  "key",
  "keys",
  "kill",
  "lag",
  "last_value",
  "lateral",
  "lead",
  "leading",
  "leave",
  "left",
  "like",
  "limit",
  "linear",
  "lines",
  "load",
  "localtime",
  "localtimestamp",
  "lock",
  "long",
  "longblob",
  "longtext",
  "loop",
  "low_priority",
  "master_bind",
  "master_ssl_verify_server_cert",
  "match",
  "maxvalue",
  "mediumblob",
  "mediumint",
  "mediumtext",
  "middleint",
  "minute_microsecond",
  "minute_second",
  "mod",
  "modifies",
  "natural",
  "not",
  "no_write_to_binlog",
  "null",
  "numeric",
  "nth_value",
  "ntile",
  "of",
  "on",
  "optimize",
  "optimizer_costs",
  "option",
  "optionally",
  "or",
  "order",
  "out",
  "outer",
  "outfile",
  "over",
  "partition",
  "percent_rank",
  "precision",
  "primary",
  "procedure",
  "purge",
  "range",
  "rank",
  "read",
  "reads",
  "read_write",
  "real",
  "recursive",
  "references",
  "regexp",
  "release",
  "rename",
  "repeat",
  "replace",
  "require",
  "resignal",
  "restrict",
  "return",
  "revoke",
  "right",
  "rlike",
  "row",
  "rows",
  "row_number",
  "schema",
  "schemas",
  "second_microsecond",
  "select",
  "sensitive",
  "separator",
  "set",
  "show",
  "signal",
  "smallint",
  "spatial",
  "specific",
  "sql",
  "sqlexception",
  "sqlstate",
  "sqlwarning",
  "sql_big_result",
  "sql_calc_found_rows",
  "sql_small_result",
  "ssl",
  "starting",
  "stored",
  "straight_join",
  "system",
  "table",
  "terminated",
  "then",
  "tinyblob",
  "tinyint",
  "tinytext",
  "to",
  "trailing",
  "trigger",
  "true",
  "undo",
  "union",
  "unique",
  "unlock",
  "unsigned",
  "update",
  "usage",
  "use",
  "using",
  "utc_date",
  "utc_time",
  "utc_timestamp",
  "values",
  "varbinary",
  "varchar",
  "varcharacter",
  "varying",
  "virtual",
  "when",
  "where",
  "while",
  "window",
  "with",
  "write",
  "xor",
  "year_month",
  "zerofill",
];

function quoteReserved(name: string): SQLExtensionValues {
  const lower = name.toLowerCase();
  const standard = POSTGRES_RESERVED_WORDS.includes(lower) ? `"${name}"` : name;
  const mysql = MYSQL_RESERVED_WORDS.includes(lower) ? `\`${name}\`` : name;

  return { postgres: standard, sqlite: standard, mysql: mysql };
}

export class TableName {
  constructor(
    private readonly utils: ChacaUtils,
    private readonly route: Route,
  ) {}

  create(name: string): TableName {
    return new TableName(this.utils, this.route.create(name));
  }

  equal(t: TableName): boolean {
    return this.route.string() === t.route.string();
  }

  value(): SQLExtensionValues {
    return quoteReserved(this.utils.pascalCase(this.route.string()));
  }
}

export class ColumnName {
  private readonly name: string;

  constructor(
    private readonly utils: ChacaUtils,
    name: string,
  ) {
    this.name = `${name}`;
  }

  equal(c: ColumnName) {
    return c.name === this.name;
  }

  value(): SQLExtensionValues {
    return quoteReserved(this.utils.snakeCase(this.name));
  }
}
